import { GlobalProvider } from "contexts/SearchContext";
import { SignUpProvider } from "contexts/SignUpContext";
import { PortfolioProvider } from "contexts/PortfolioContext";
import "styles/globals.css";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import type { AppContext, AppProps } from "next/app";
import Image from "next/image";
import { SessionProvider, useSession } from "next-auth/react";
import { useRouter } from "next/router";
import { appWithTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { GetServerSidePropsContext, GetStaticPropsContext } from "next";
import "react-tooltip/dist/react-tooltip.css";
import UAParser from "ua-parser-js";
import { Tooltip } from "react-tooltip";
import Layout from "components/layouts/Layout";
import { NextPage } from "next";
import { useEffect, useState } from "react";
import { PortfolioDetailProvider } from "contexts/PortfolioBackContext";
import {
  QueryClient,
  QueryClientProvider,
  HydrationBoundary,
} from "@tanstack/react-query";
import { RecoilRoot } from "recoil";
import Head from "next/head";
import { NotificationsProvider } from "contexts/NotificationContext";
import {
  InterestToastProvider,
  TrackedGlobalAlarmModal,
} from "contexts/InterestContext";
import GlobalToast, {
  GlobalPortfolioToast,
} from "components/atoms/GlobalToast";
import GlobalRiskAlarmNotiModal from "components/atoms/GlobalAlarmModal";
import { SortProvider } from "contexts/SortContext";
import * as Sentry from "@sentry/nextjs";
import { KBProvider } from "contexts/KBContext";
import ErrorPage from "./_error";
// Analytics SDK
import { initAnalytics, setUser } from "lib/analytics/core/client";
import { AnalyticsProvider, ScreenTracking } from "lib/analytics/react/screen";
import { loadConfigFromEnv } from "lib/analytics/core/config";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { useLoginIntent } from "utils/hooks/LoginIntent";
import WebMcpHost from "components/webmcp/WebMcpHost";

function SentryUserManager() {
  const { data: session } = useSession();

  useEffect(() => {
    if (session && session.user) {
      Sentry.setUser({
        id: session.user.email as string,
        username: session.user.name as string,
      });
      // Analytics 사용자 설정
      // setUser(session.user.email as string);
      // Sentry.captureException("user login");
    } else {
      Sentry.setUser(null);
      // Analytics 사용자 해제
      // setUser(null);
    }
  }, [session]);
  return null;
}

const SITE_URL =
  process.env.NEXT_PUBLIC_NEXTAUTH_URL || "https://riskweather.io";

/**
 * URL마다 self-canonical + ko/en hreflang 을 붙인다.
 * sitemap 의 hreflang 과 짝이 맞아야 구글이 두 locale 을 중복이 아닌 대체본으로 본다.
 */
function LocaleLinks() {
  const router = useRouter();
  const locale = router.locale === "en" ? "en" : "ko";
  // 쿼리·해시가 다른 주소는 같은 문서로 합친다.
  const path = router.asPath.split(/[?#]/)[0].replace(/\/$/, "");
  const href = (target: string) => `${SITE_URL}/${target}${path}`;

  return (
    <Head>
      <link rel="canonical" href={href(locale)} />
      <link rel="alternate" hrefLang="ko" href={href("ko")} />
      <link rel="alternate" hrefLang="en" href={href("en")} />
      <link rel="alternate" hrefLang="x-default" href={href("ko")} />
    </Head>
  );
}

function App({ Component, pageProps }: AppProps & { Component: NextPage }) {
  const router = useRouter();
  const isErrorPage = Component === ErrorPage;
  const { status, data: session } = useSession();
  const [isInitialized, setIsInitialized] = useState(false);
  useLoginIntent(isInitialized);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1분
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  useEffect(() => {
    if (
      "scrollRestoration" in history &&
      history.scrollRestoration !== "manual"
    ) {
      history.scrollRestoration = "manual";
    }
    const updateHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };

    updateHeight(); // 초기 실행
    window.addEventListener("resize", updateHeight); // 화면 크기 변경 시 업데이트
    return () => window.removeEventListener("resize", updateHeight); // 클린업
  }, [router.pathname]);

  // Analytics 초기화
  useEffect(() => {
    if (status === "loading") return;
    const envConfig = loadConfigFromEnv();
    initAnalytics({
      endpoint: "/api/track",
      debug: false, //process.env.NODE_ENV !== "production",
      bufMax: 10,
      bufMs: 3000,
      ...envConfig,
    });
    setUser((session?.user?.email as string) || null);
    setIsInitialized(true);
  }, [status]);

  const { hasMetaData } = pageProps;
  const flag = hasMetaData ? hasMetaData : false;
  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={pageProps.dehydratedState}>
        <AnalyticsProvider>
          <ScreenTracking initialized={isInitialized} />
          <KBProvider>
            <GlobalProvider>
              <NotificationsProvider>
                <PortfolioDetailProvider>
                  <PortfolioProvider>
                    <SignUpProvider>
                      <InterestToastProvider>
                        <SortProvider>
                          <Layout {...pageProps} isErrorPage={isErrorPage}>
                            <GlobalToast />
                            <GlobalPortfolioToast />
                            <TrackedGlobalAlarmModal>
                              <GlobalRiskAlarmNotiModal />
                            </TrackedGlobalAlarmModal>
                            <LocaleLinks />
                            {!flag && (
                              <Head>
                                <link
                                  rel="Icon"
                                  href="/images/favicon.png"
                                  type="image/x-icon"
                                />
                                <title>Riskweather</title>
                                <meta
                                  property="og:image"
                                  content={`https://riskweather.io/images/Thumb.png`}
                                />
                                <meta
                                  property="og:description"
                                  content={
                                    router.locale === "ko"
                                      ? "투자 리스크 확인부터 관리까지, 모든 판단을 리스크웨더에서"
                                      : "Invest with Confidence, We've Got the Risks Covered"
                                  }
                                />
                                <meta
                                  name="description"
                                  content={
                                    "투자 리스크 확인부터 관리까지, 모든 판단을 리스크웨더에서 "
                                  }
                                />
                                <meta
                                  property="og:title"
                                  content="리스크웨더 - 개인투자자를 보호합니다."
                                />
                                <meta
                                  name="twitter:title"
                                  content="리스크웨더 - 개인투자자를 보호합니다."
                                />
                                <meta
                                  name="twitter:description"
                                  content={
                                    "투자 리스크 확인부터 관리까지, 모든 판단을 리스크웨더에서 "
                                  }
                                />
                                <meta
                                  name="twitter:image"
                                  content={`https://riskweather.io/images/Thumb.png`}
                                />
                                <meta name="twitter:card" content={`summary`} />
                              </Head>
                            )}

                            <Component {...pageProps} />
                            <SentryUserManager />
                            <ToastContainer />
                            <Tooltip
                              id="riskLevel"
                              style={{
                                zIndex: 289,
                                fontSize: "small",
                                fontWeight: "bold",
                              }}
                            />
                            <Tooltip
                              id="riskIndexExplain"
                              style={{
                                zIndex: 289,
                                fontSize: "small",
                                fontWeight: "bold",
                                backgroundColor: "transparent",
                              }}
                            >
                              <Image
                                src={
                                  router.locale == "ko"
                                    ? "/images/explain/RiskIndexExplain.svg"
                                    : "/images/explain/RiskIndexExplainEng.svg"
                                }
                                alt=""
                                width={350}
                                height={150}
                              />
                            </Tooltip>
                            <Tooltip
                              id="traitExplain"
                              style={{
                                zIndex: 289,
                                fontSize: "small",
                                fontWeight: "bold",
                                backgroundColor: "transparent",
                              }}
                            >
                              <Image
                                src={
                                  router.locale == "ko"
                                    ? "/images/traits/explanationKr.svg"
                                    : "/images/traits/explanation.svg"
                                }
                                alt=""
                                width={350}
                                height={150}
                              />
                            </Tooltip>
                            {process.env.NODE_ENV === "development" && (
                              <ReactQueryDevtools initialIsOpen={false} />
                            )}
                            <div id="portal"></div>
                            <WebMcpHost />
                          </Layout>
                        </SortProvider>
                      </InterestToastProvider>
                    </SignUpProvider>
                  </PortfolioProvider>
                </PortfolioDetailProvider>
              </NotificationsProvider>
            </GlobalProvider>
          </KBProvider>
        </AnalyticsProvider>
      </HydrationBoundary>
    </QueryClientProvider>
  );
}

const AppWithI18n = appWithTranslation(App);

const AppWithAuth = (props: AppProps) => (
  <>
    <RecoilRoot>
      <SessionProvider session={props.pageProps.session}>
        <AppWithI18n {...props} />
      </SessionProvider>
    </RecoilRoot>
  </>
);

AppWithAuth.getInitialProps = async ({ ctx }: AppContext) => {
  const userAgent: string | undefined = ctx.req
    ? ctx.req.headers["user-agent"]
    : navigator.userAgent;

  const parser = new UAParser();
  let isMobile = false;

  if (userAgent) {
    const result = parser.setUA(userAgent).getResult();
    isMobile = result.device && result.device.type === "mobile";
  }
  // const result = parser.setUA(userAgent).getResult();
  // const isMobile = result.device && result.device.type === 'mobile';
  // const isMobi = userAgent?.indexOf("Mobi") !== -1;
  // const isMobi = Boolean(userAgent?.match(
  //   /Android|BlackBerry|iPhone|Galaxy|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i,
  // ) );
  // const width = typeof window !== 'undefined' ? window.innerWidth : screen.width;
  // const isMobileWidth = width !== null && width < 1180;
  // const isMobile = isMobi ;

  return { pageProps: { isMobile } };
};

export default AppWithAuth;

export async function getStaticProps(
  ctx: GetStaticPropsContext,
  { locale = "ko" }: GetStaticPropsContext
) {
  return {
    props: { ...(await serverSideTranslations(locale as string, ["common"])) },
  };
}
